import React, { useContext, useMemo } from 'react';
import { GetFeatureFlagsDocument } from 'src/graphql/lib/typed-documents';
import { useTypedQuery } from 'src/graphql/hooks/useTypedApollo';
import { useNullableSessionUser } from './sessionContext';

interface Value {
    organizationId?: string;
    facilityId?: string;
}

interface Props {
    facilityId?: string;
    organizationId?: string;
    children?: React.ReactNode;
}

const context = React.createContext<Value | null>(null);

export const FeatureFlagsProvider = ({ facilityId, organizationId, children }: Props) => {
    const parentContextValue = useContext(context);

    // useMemo required - context value passed to provider.
    // Without stable reference, all context consumers re-render on every parent render.
    const mergedValue = useMemo(
        () => ({
            organizationId: organizationId ?? parentContextValue?.organizationId,
            facilityId,
        }),
        [parentContextValue?.organizationId, facilityId, organizationId],
    );

    return <context.Provider value={mergedValue}>{children}</context.Provider>;
};

interface FeatureFlagsParams {
    facilityId?: string | null;
    organizationId?: string | null;
}

export const useFeatureFlags = ({ facilityId, organizationId }: FeatureFlagsParams = {}) => {
    const me = useNullableSessionUser();
    const value = useContext(context);

    const { data, refetch, loading } = useTypedQuery(GetFeatureFlagsDocument, {
        variables: {
            facilityId: facilityId ?? value?.facilityId ?? undefined,
            organizationId: organizationId ?? value?.organizationId ?? undefined,
        },
        skip: !me,
        fetchPolicy: 'cache-first',
    });

    const featureFlags = data?.featureFlags ?? [];

    return { featureFlags, refetch, loading };
};
